/**
 * Form controls.
 *
 * `Field` owns label association: if its single child has no `id`, one is
 * generated and injected, so `<Field label="Email"><Input/></Field>` is always
 * correctly labelled without the caller thinking about it. It also owns the
 * hint/error slot — an error replaces the hint rather than stacking with it.
 */
import { cloneElement, isValidElement, useId } from 'react';
import type {
  InputHTMLAttributes,
  ReactElement,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react';
import { cx } from '@/lib/cx';
import { Icon } from '../icons';

export function Field({ label, hint, error, required, children, className }: {
  label?: string;
  hint?: ReactNode;
  error?: string;
  /** Adds the required marker to the label. Set `required` on the control too. */
  required?: boolean;
  children: ReactNode;
  className?: string;
}) {
  const autoId = useId();
  const errorId = `${autoId}-err`;
  let control = children;
  let controlId: string | undefined;

  if (isValidElement<{ id?: string; 'aria-invalid'?: boolean; 'aria-describedby'?: string }>(children)) {
    const child = children as ReactElement<{ id?: string; 'aria-invalid'?: boolean; 'aria-describedby'?: string }>;
    controlId = child.props.id ?? (label ? autoId : undefined);
    control = cloneElement(child, {
      ...(label && !child.props.id ? { id: autoId } : {}),
      ...(error ? { 'aria-invalid': true, 'aria-describedby': errorId } : {}),
    });
  }

  return (
    <div className={cx('field', className)}>
      {label && (
        <label htmlFor={controlId}>
          {label}
          {required && <span className="req" aria-hidden>*</span>}
        </label>
      )}
      {control}
      {error ? (
        <span className="err" id={errorId} role="alert">
          <Icon.Alert aria-hidden />
          {error}
        </span>
      ) : hint ? (
        <span className="hint">{hint}</span>
      ) : null}
    </div>
  );
}

export function Input({ className, ...props }: InputHTMLAttributes<HTMLInputElement>) {
  return <input className={cx('input', className)} {...props} />;
}

export function Textarea({ className, ...props }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea className={cx('textarea', className)} {...props} />;
}

export function Select({ className, ...props }: SelectHTMLAttributes<HTMLSelectElement>) {
  return <select className={cx('select', className)} {...props} />;
}

/** Text input with a leading magnifier — filter boxes and list search. */
export function SearchInput({ className, ...props }: InputHTMLAttributes<HTMLInputElement>) {
  return (
    <span className={cx('input-affix', className)}>
      <Icon.Search aria-hidden />
      <input type="search" className="input" {...props} />
    </span>
  );
}

export function Checkbox({ label, checked, onChange, disabled, hint }: {
  label: ReactNode;
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
  hint?: string;
}) {
  return (
    <label className="check" title={hint}>
      <input type="checkbox" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
      {label}
    </label>
  );
}

/** For settings that apply immediately — no Save button in sight. */
export function Switch({ label, checked, onChange, disabled }: {
  label: ReactNode;
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <label className="switch">
      <input type="checkbox" role="switch" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
      <span>{label}</span>
    </label>
  );
}
